// Client auth state for the hosted web client.
//
// The client (Cloudflare Pages) and backend (the Cloudflare Worker) are different origins, so a backend session
// cookie would be third-party and unreliable. Instead auth is an opaque token: the hosted /login
// Discord broker (opened in a popup) mints it, we stash it in localStorage, and the socket replays
// it as a {t:"Auth"} message on every (re)connect. Logout is sent over the socket ({t:"Logout"})
// so the server can revoke it, see net store + World.svelte.

import { AUTH_BASE } from "./config.ts";

const TOKEN_KEY = "df.token";

/** The current login token, or null if signed out. */
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);

export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

/** Absolute URL of the hosted Discord login broker. Opened in a popup; the broker mints a token and
 *  posts it back via postMessage (with a copy-paste code fallback if the window can't talk back). */
export const LOGIN_URL = `${AUTH_BASE}/api/login`;

// ── username + password (verified via Discord) ────────────────────────────────────────────────────
// These hit the auth Worker's /api/account/* routes directly (cross-origin fetch, no cookies). On
// success the server returns the same opaque token the Discord popup mints; the caller stores it and
// reconnects, exactly like the OAuth path. Verification + password reset happen IN Discord (the user
// runs /verify / /reset there); here we just register, log in, and confirm a reset code.

/** Thrown on a non-2xx response; `data` carries the JSON body (e.g. { needsVerification: true }). */
export class AuthError extends Error {
  constructor(
    message: string,
    readonly data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function post(path: string, body: object): Promise<Record<string, unknown>> {
  let res: Response;
  try {
    res = await fetch(`${AUTH_BASE}/api/account${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthError("Couldn't reach the auth server. Check your connection and retry.");
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new AuthError((data.error as string) ?? "Something went wrong.", data);
  return data;
}

/** Create an account → returns { code, inviteUrl }. The user joins Discord and runs /verify <code>
 *  there; then they sign in (the account is unverified until the Discord step completes). */
export const registerAccount = (username: string, password: string) =>
  post("/register", { username, password }) as Promise<{ code: string; inviteUrl: string | null }>;

/** Sign in → returns { token }; throws AuthError with data.needsVerification when not yet verified. */
export const loginAccount = (username: string, password: string) =>
  post("/login", { username, password });

/** Set a new password with a reset code obtained from /reset in Discord → returns { token }. */
export const confirmReset = (code: string, newPassword: string) =>
  post("/reset/confirm", { code, newPassword });
